import { useEffect, useState, useCallback } from "react";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { TAXONOMY, getSubcategories, CATEGORY_LABELS } from "@/lib/taxonomy";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileUpload, fileToUrl } from "@/components/FileUpload";
import {
  Plus, Pencil, Trash2, Heart, Sparkles, Cake, Salad, Coffee, Upload, Loader2, X, Wand2, Eye, EyeOff, Droplet, Tags,
} from "lucide-react";

const CATEGORIES = [
  { v: "sweet", l: "حلو", icon: Cake },
  { v: "savory", l: "مالح", icon: Salad },
  { v: "beverage", l: "مشروبات", icon: Coffee },
  { v: "sauces", l: "صلصات", icon: Droplet },
];
const CAT_LABEL = CATEGORY_LABELS;

const empty = {
  title: "", image_url: "", short_description: "", ingredients: [], steps: [],
  prep_minutes: 5, cook_minutes: 5, servings: 1, calories_per_serving: 0,
  protein_g: 0, carbs_g: 0, fat_g: 0, category: "savory", subcategory: "",
  difficulty: "easy", is_featured: false, status: "published",
};

export default function AdminRecipes() {
  const [recipes, setRecipes] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [aiOpen, setAiOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [catFilter, setCatFilter] = useState("");
  const [subFilter, setSubFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | "draft" | "published"
  const [regenImgLoading, setRegenImgLoading] = useState(false);
  const [reclassifyLoading, setReclassifyLoading] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);

  const runGenerateNotes = async () => {
    const ok = window.confirm(
      "سيتم توليد فوائد صحية / رياضية للوصفات التي ليس لديها فائدة بعد.\n\n" +
      "العملية تعمل في الخلفية وقد تستغرق دقيقة إلى ثلاث دقائق. " +
      "يمكنك متابعة عملك أثناء ذلك — سيظهر تنبيه فور الانتهاء.\n\n" +
      "هل تريد المتابعة؟"
    );
    if (!ok) return;
    setNotesLoading(true);
    try {
      const { data } = await api.post(
        "/recipes/admin/backfill-fitness-notes?only_missing=true",
        null,
        { timeout: 30000 },
      );
      const jobId = data?.job_id;
      const target = data?.target ?? 0;
      if (!jobId) throw new Error("لم يرجع معرف المهمة");

      toast.success(
        `بدأ توليد الفوائد في الخلفية — ${target} وصفة. سيظهر تنبيه فور الانتهاء.`,
        { duration: 7000 },
      );

      // Poll status every 5 s in the background. On finish → toast + refresh.
      const start = Date.now();
      const MAX_MS = 10 * 60 * 1000;
      const poll = async () => {
        while (Date.now() - start < MAX_MS) {
          await new Promise((r) => setTimeout(r, 5000));
          try {
            const { data: st } = await api.get(`/recipes/admin/backfill-fitness-notes/status/${jobId}`);
            if (st?.status === "done") {
              toast.success(
                `اكتمل — تم توليد فوائد لـ ${st.written ?? 0} من ${st.scanned ?? 0} وصفة.`,
                { duration: 9000 },
              );
              await load();
              return;
            }
            if (st?.status === "error") {
              toast.error(`فشل توليد الفوائد — ${st.error || "خطأ غير معروف"}`);
              return;
            }
          } catch {
            /* keep polling */
          }
        }
        toast("لا تزال المهمة تعمل في الخلفية — تحقق لاحقاً.", { duration: 6000 });
      };
      poll();  // fire-and-forget — button becomes clickable again immediately
    } catch (e) {
      toast.error(extractError(e, "فشل بدء توليد الفوائد — حاول مجدداً"));
    } finally {
      setNotesLoading(false);
    }
  };
  // Background AI-generate jobs the user has fired off. Each entry:
  // { id, label, startedAt }. The tracker polls each every 4s and, when it
  // finishes, drops it and refreshes the recipe list so new drafts appear.
  const [bgJobs, setBgJobs] = useState([]);

  // Poll every active background job every 4 s. When one finishes, drop it
  // from the tracker, refresh recipes, and notify the admin.
  useEffect(() => {
    if (bgJobs.length === 0) return;
    let cancelled = false;
    const interval = setInterval(async () => {
      for (const job of bgJobs) {
        try {
          const { data } = await api.get(`/recipes/admin/ai-generate/status/${job.id}`);
          if (cancelled) return;
          if (data.status === "done") {
            toast.success(`اكتمل توليد "${job.label}" — تم حفظ ${data.count || 0} وصفة كمسودات.`, { duration: 8000 });
            setBgJobs((prev) => prev.filter((j) => j.id !== job.id));
            load();
          } else if (data.status === "error") {
            toast.error(`فشل توليد "${job.label}" — ${data.error || "خطأ غير معروف"}`);
            setBgJobs((prev) => prev.filter((j) => j.id !== job.id));
          }
        } catch {
          // Silent — will retry on next tick
        }
      }
    }, 4000);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgJobs.length]);

  // Force a re-render every 3 s so the elapsed-time chips tick visibly.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (bgJobs.length === 0) return;
    const t = setInterval(() => forceTick((n) => n + 1), 3000);
    return () => clearInterval(t);
  }, [bgJobs.length]);

  const runReclassifyAll = async () => {
    const ok = window.confirm(
      "سيتم إعادة تصنيف كل وصفات المنصة والوصفات المستوردة لكل الأعضاء ضمن التصنيفات الجديدة.\n\n" +
      "الذكاء الاصطناعي (Gemini) يقرأ الاسم والوصف والمكونات وأول الخطوات لكل وصفة ويضعها في التصنيف الصحيح. " +
      "العملية تعمل في الخلفية وقد تستغرق دقيقتين إلى خمس دقائق حسب حجم الكتالوج. " +
      "يمكنك متابعة عملك أثناء ذلك — سيظهر تنبيه فور الانتهاء.\n\n" +
      "هل تريد المتابعة؟"
    );
    if (!ok) return;
    setReclassifyLoading(true);
    try {
      const { data } = await api.post(
        "/recipes/admin/backfill-subcategories?reclassify_all=true",
        null,
        { timeout: 30000 },
      );
      const jobId = data?.job_id;
      const target = data?.target ?? 0;
      if (!jobId) throw new Error("لم يرجع معرف المهمة");

      toast.success(
        `بدأت إعادة التصنيف في الخلفية — ${target} وصفة. سيظهر تنبيه فور الانتهاء.`,
        { duration: 7000 },
      );

      // Poll status every 5 s. On finish → toast + refresh.
      const start = Date.now();
      const MAX_MS = 10 * 60 * 1000;
      const poll = async () => {
        while (Date.now() - start < MAX_MS) {
          await new Promise((r) => setTimeout(r, 5000));
          try {
            const { data: st } = await api.get(`/recipes/admin/backfill-subcategories/status/${jobId}`);
            if (st?.status === "done") {
              toast.success(
                `تم — فحص ${st.scanned ?? 0} وصفة، ${st.classified ?? 0} صُنّفت، ${st.recategorized ?? 0} نُقلت لفئة مختلفة.`,
                { duration: 9000 },
              );
              await load();
              return;
            }
            if (st?.status === "error") {
              toast.error(`فشل إعادة التصنيف — ${st.error || "خطأ غير معروف"}`);
              return;
            }
          } catch {
            /* keep polling */
          }
        }
        toast("لا تزال المهمة تعمل في الخلفية — تحقق لاحقاً.", { duration: 6000 });
      };
      poll(); // fire-and-forget — button becomes clickable again immediately
    } catch (e) {
      toast.error(extractError(e, "فشل بدء إعادة التصنيف — حاول مجدداً بعد قليل"));
    } finally {
      setReclassifyLoading(false);
    }
  };

  const load = useCallback(
    () => api.get("/recipes/admin/list").then((r) => setRecipes(r.data)),
    []
  );
  useEffect(() => { load(); }, [load]);

  const regenerateImage = async () => {
    const title = (f.title || "").trim();
    if (!title) { toast.error("اكتب عنوان الوصفة أولاً"); return; }
    setRegenImgLoading(true);
    try {
      const { data } = await api.post("/recipes/admin/regenerate-image", {
        title,
        category: f.category || "savory",
        short_description: f.short_description || "",
        ingredients: f.ingredients || [],
      }, { timeout: 120000 });
      setF((prev) => ({ ...prev, image_url: data.image_url }));
      toast.success("تم توليد الصورة");
    } catch (e) {
      toast.error(extractError(e, "فشل توليد الصورة"));
    } finally { setRegenImgLoading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...f,
        steps: typeof f.steps === "string" ? f.steps.split("\n").map((s) => s.trim()).filter(Boolean) : f.steps,
      };
      if (f.id) await api.patch(`/recipes/admin/${f.id}`, payload);
      else await api.post("/recipes/admin", payload);
      toast.success("تم الحفظ");
      setOpen(false); setF(empty); load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const edit = (r) => {
    setF({ ...r, steps: (r.steps || []).join("\n") });
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("حذف الوصفة؟")) return;
    await api.delete(`/recipes/admin/${id}`); toast.success("تم الحذف"); load();
  };

  // Flip a single recipe between draft and published.
  const togglePublish = async (r) => {
    const next = r.status === "draft" ? "published" : "draft";
    try {
      await api.patch(`/recipes/admin/${r.id}`, { title: r.title, status: next });
      toast.success(next === "published" ? "تم النشر" : "تم إرجاعها لمسودة");
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  // Publish every draft in one click after an AI batch.
  const publishAllDrafts = async () => {
    const ids = recipes.filter((r) => r.status === "draft").map((r) => r.id);
    if (ids.length === 0) return;
    if (!window.confirm(`نشر ${ids.length} مسودة دفعة واحدة؟`)) return;
    try {
      await api.post("/recipes/admin/bulk-publish", { ids, status: "published" });
      toast.success(`تم نشر ${ids.length} وصفة`);
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const draftCount = recipes.filter((r) => r.status === "draft").length;

  const shown = recipes.filter((r) => {
    if (catFilter && r.category !== catFilter) return false;
    if (subFilter && r.subcategory !== subFilter) return false;
    if (statusFilter && (r.status || "published") !== statusFilter) return false;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="admin-recipes">
      {bgJobs.length > 0 && (
        <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/30 p-3" data-testid="bg-jobs-tracker">
          <div className="flex items-center gap-2 text-sm font-medium text-purple-900 dark:text-purple-100 mb-1.5">
            <Loader2 className="h-4 w-4 animate-spin" />
            {bgJobs.length === 1 ? "توليد نشط في الخلفية" : `${bgJobs.length} توليدات نشطة في الخلفية`}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {bgJobs.map((j) => {
              const secs = Math.floor((Date.now() - j.startedAt) / 1000);
              const mm = String(Math.floor(secs / 60)).padStart(2, "0");
              const ss = String(secs % 60).padStart(2, "0");
              return (
                <span
                  key={j.id}
                  className="inline-flex items-center gap-1.5 text-[11px] bg-white dark:bg-purple-900/40 border border-purple-200 dark:border-purple-700 rounded-full px-2 py-0.5"
                  data-testid={`bg-job-${j.id}`}
                >
                  <Sparkles className="h-3 w-3 text-purple-600" />
                  {j.label}
                  <span className="text-purple-500/70 font-mono text-[10px]">{mm}:{ss}</span>
                </span>
              );
            })}
          </div>
          <p className="text-[11px] text-purple-800/70 dark:text-purple-100/70 mt-1.5">
            يمكنك بدء توليد آخر أو التنقل بين الصفحات — سيظهر تنبيه فور اكتمال أي توليد.
          </p>
        </div>
      )}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold">إدارة الوصفات</h1>
          <p className="text-sm text-foreground/60 mt-1">تصنيف تلقائي: حلو / مالح / مشروبات / صلصات. توليد بالذكاء الاصطناعي أو استيراد PDF/صور.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={runGenerateNotes}
            disabled={notesLoading}
            className="gap-2 rounded-full"
            data-testid="btn-generate-notes"
            title="يولّد فائدة صحية / رياضية للوصفات التي ليس لديها فائدة"
          >
            {notesLoading ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> جاري توليد الفوائد...</>
            ) : (
              <><Sparkles className="h-4 w-4 text-emerald-600" /> توليد الفوائد للوصفات القديمة</>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={runReclassifyAll}
            disabled={reclassifyLoading}
            className="gap-2 rounded-full"
            data-testid="btn-reclassify-all"
            title="يعيد تصنيف كل وصفات المنصة والأعضاء تلقائياً في التصنيفات الجديدة"
          >
            {reclassifyLoading ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> جاري إعادة التصنيف...</>
            ) : (
              <><Tags className="h-4 w-4 text-emerald-600" /> إعادة تصنيف الكل</>
            )}
          </Button>
          <Button variant="outline" onClick={() => setGenOpen(true)} className="gap-2 rounded-full" data-testid="btn-ai-generate">
            <Sparkles className="h-4 w-4 text-purple-600" /> توليد وصفات جديدة
          </Button>
          <Button variant="outline" onClick={() => setAiOpen(true)} className="gap-2 rounded-full" data-testid="btn-ai-import">
            <Sparkles className="h-4 w-4 text-primary" /> استخراج من PDF/صورة
          </Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setF(empty); }}>
            <DialogTrigger asChild>
              <Button className="gap-2 rounded-full" data-testid="btn-new-recipe"><Plus className="h-4 w-4" /> وصفة يدوية</Button>
            </DialogTrigger>
            <DialogContent dir="rtl" className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{f.id ? "تعديل الوصفة" : "وصفة جديدة"}</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-3">
                <div className="space-y-2"><Label>العنوان</Label><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="recipe-title-input" /></div>
                <div className="space-y-2"><Label>وصف قصير</Label><Input value={f.short_description} onChange={(e) => setF({ ...f, short_description: e.target.value })} /></div>
                <div className="space-y-2"><Label>الصورة</Label>
                  <FileUpload kind="image" value={f.image_url} onChange={(v) => setF({ ...f, image_url: fileToUrl(v) })} testid="recipe-image-upload" />
                  <div className="flex items-center gap-2 flex-wrap pt-1">
                    <Button
                      type="button"
                      onClick={regenerateImage}
                      disabled={regenImgLoading}
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      data-testid="btn-recipe-ai-image"
                    >
                      {regenImgLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 text-purple-600" />}
                      {f.image_url ? "توليد صورة أخرى بالذكاء" : "توليد صورة بالذكاء الاصطناعي"}
                    </Button>
                    <span className="text-[11px] text-muted-foreground">
                      تحتاج عنواناً وتصنيفاً — النتيجة صورة احترافية بأسلوب مجلات الطبخ.
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>التصنيف الرئيسي</Label>
                    <Select value={f.category || "savory"} onValueChange={(v) => setF({ ...f, category: v, subcategory: "" })}>
                      <SelectTrigger data-testid="recipe-category-input"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>التصنيف الفرعي</Label>
                    <Select value={f.subcategory || "__none"} onValueChange={(v) => setF({ ...f, subcategory: v === "__none" ? "" : v })}>
                      <SelectTrigger data-testid="recipe-subcategory-input"><SelectValue placeholder="اختر (اختياري)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">— بدون تصنيف فرعي —</SelectItem>
                        {getSubcategories(f.category || "savory").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-2"><Label>تحضير (د)</Label><Input type="number" min={0} value={f.prep_minutes} onChange={(e) => setF({ ...f, prep_minutes: +e.target.value })} /></div>
                  <div className="space-y-2"><Label>طبخ (د)</Label><Input type="number" min={0} value={f.cook_minutes} onChange={(e) => setF({ ...f, cook_minutes: +e.target.value })} /></div>
                  <div className="space-y-2"><Label>حصص</Label><Input type="number" min={1} value={f.servings} onChange={(e) => setF({ ...f, servings: +e.target.value })} /></div>
                  <div className="space-y-2"><Label>سعرات/حصة</Label><Input type="number" min={0} value={f.calories_per_serving} onChange={(e) => setF({ ...f, calories_per_serving: +e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2"><Label>بروتين (غ)</Label><Input type="number" min={0} step="0.1" value={f.protein_g} onChange={(e) => setF({ ...f, protein_g: +e.target.value })} /></div>
                  <div className="space-y-2"><Label>كربوهيدرات (غ)</Label><Input type="number" min={0} step="0.1" value={f.carbs_g} onChange={(e) => setF({ ...f, carbs_g: +e.target.value })} /></div>
                  <div className="space-y-2"><Label>دهون (غ)</Label><Input type="number" min={0} step="0.1" value={f.fat_g} onChange={(e) => setF({ ...f, fat_g: +e.target.value })} /></div>
                </div>
                <IngredientsEditor value={f.ingredients || []} onChange={(v) => setF({ ...f, ingredients: v })} />
                <div className="space-y-2"><Label>خطوات التحضير (كل خطوة في سطر)</Label>
                  <Textarea rows={6} value={typeof f.steps === "string" ? f.steps : (f.steps || []).join("\n")} onChange={(e) => setF({ ...f, steps: e.target.value })} data-testid="recipe-steps" />
                </div>
                <Button type="submit" className="w-full rounded-full" data-testid="recipe-save">حفظ</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Category filter chips */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => { setCatFilter(""); setSubFilter(""); }} className={`px-3 py-1.5 rounded-full text-xs border-2 ${catFilter === "" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`} data-testid="admin-cat-all">
          الكل ({recipes.length})
        </button>
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const n = recipes.filter((r) => r.category === c.v).length;
          return (
            <button key={c.v} onClick={() => { setCatFilter(c.v); setSubFilter(""); }} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border-2 ${catFilter === c.v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`} data-testid={`admin-cat-${c.v}`}>
              <Icon className="h-3 w-3" /> {c.l} ({n})
            </button>
          );
        })}
      </div>

      {/* Level-2 subcategory chips — mirror the member app.
          Shown only when a main category is picked; count is scoped to that category
          (and honors the current status filter so numbers stay coherent). */}
      {catFilter && getSubcategories(catFilter).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="admin-subcategory-chips">
          <button
            onClick={() => setSubFilter("")}
            className={`px-3 py-1 rounded-full text-[11px] border ${
              !subFilter ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-muted-foreground hover:border-primary/40"
            }`}
            data-testid="admin-sub-all"
          >
            كل الأنواع
          </button>
          {getSubcategories(catFilter).map((s) => {
            const n = recipes.filter((r) => (
              r.category === catFilter &&
              r.subcategory === s &&
              (!statusFilter || (r.status || "published") === statusFilter)
            )).length;
            const active = subFilter === s;
            return (
              <button
                key={s}
                onClick={() => setSubFilter(active ? "" : s)}
                className={`px-3 py-1 rounded-full text-[11px] border transition-colors ${
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card hover:border-primary/40"
                }`}
                data-testid={`admin-sub-${s}`}
              >
                {s} ({n})
              </button>
            );
          })}
        </div>
      )}

      {/* Status filter chips — split drafts (AI auto-saved) from published */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setStatusFilter("")} className={`px-3 py-1.5 rounded-full text-xs border-2 ${statusFilter === "" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`} data-testid="admin-status-all">
          كل الحالات
        </button>
        <button onClick={() => setStatusFilter("published")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border-2 ${statusFilter === "published" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`} data-testid="admin-status-published">
          <Eye className="h-3 w-3" /> منشورة ({recipes.filter((r) => (r.status || "published") === "published").length})
        </button>
        <button onClick={() => setStatusFilter("draft")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border-2 ${statusFilter === "draft" ? "border-amber-500 bg-amber-500 text-white" : "border-amber-500/50 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200"}`} data-testid="admin-status-draft">
          <EyeOff className="h-3 w-3" /> مسودات ({draftCount})
        </button>
        {draftCount > 0 && (
          <Button size="sm" onClick={publishAllDrafts} className="gap-1 rounded-full ms-auto bg-emerald-600 hover:bg-emerald-700" data-testid="btn-publish-all-drafts">
            <Eye className="h-3.5 w-3.5" /> نشر كل المسودات ({draftCount})
          </Button>
        )}
      </div>

      {shown.length === 0 && (
        <div className="text-center text-sm text-muted-foreground py-12 border border-dashed rounded-xl">
          لا توجد وصفات. ابدأ باستخراج مجموعة عبر الذكاء الاصطناعي أو أضف يدوياً.
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((r) => {
          const isDraft = (r.status || "published") === "draft";
          return (
          <Card key={r.id} className={`overflow-hidden ${isDraft ? "ring-2 ring-amber-400/70" : ""}`} data-testid={`admin-recipe-${r.id}`}>
            {r.image_url ? (
              <div className="relative">
                <img src={r.image_url} alt={r.title} className="w-full aspect-video object-cover" />
                {r.ai_generated_image && (
                  <span
                    className="absolute top-1.5 start-1.5 inline-flex items-center gap-1 text-[10px] font-medium bg-purple-600/90 text-white rounded-full px-2 py-0.5 backdrop-blur-sm"
                    title="صورة مولّدة تلقائياً بالذكاء الاصطناعي — لم توجد في الملف الأصلي"
                    data-testid={`badge-ai-image-${r.id}`}
                  >
                    <Sparkles className="h-3 w-3" /> صورة AI
                  </span>
                )}
              </div>
            ) : (
              <div className="w-full aspect-video bg-secondary grid place-items-center text-muted-foreground">
                {(() => { const C = CATEGORIES.find((c) => c.v === r.category)?.icon; return C ? <C className="h-10 w-10 opacity-40" /> : null; })()}
              </div>
            )}
            <div className="p-3 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-display font-semibold truncate flex-1">{r.title}</h3>
                <span className="text-[10px] bg-primary/10 text-primary rounded-full px-2 py-0.5 shrink-0">{CAT_LABEL[r.category] || r.category}</span>
              </div>
              {r.subcategory && (
                <span className="inline-block text-[10px] bg-secondary text-secondary-foreground rounded-full px-2 py-0.5" data-testid={`sub-chip-${r.id}`}>
                  {r.subcategory}
                </span>
              )}
              {isDraft && (
                <div className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200 rounded-full px-2 py-0.5">
                  <EyeOff className="h-3 w-3" /> مسودة — غير مرئية للأعضاء
                </div>
              )}
              {r.admin_note && (
                <p
                  className="text-[10px] text-purple-800 dark:text-purple-200 bg-purple-50 dark:bg-purple-950/40 rounded p-1.5 leading-relaxed"
                  data-testid={`admin-note-${r.id}`}
                >
                  <Sparkles className="h-3 w-3 inline align-text-top me-1" />
                  {r.admin_note}
                </p>
              )}
              {r.fitness_note && (
                <p
                  className="text-[10px] text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-950/40 rounded p-1.5 leading-relaxed"
                  data-testid={`fitness-note-${r.id}`}
                >
                  💪 {r.fitness_note}
                </p>
              )}
              <p className="text-xs text-muted-foreground line-clamp-2 min-h-[32px]">{r.short_description}</p>
              <div className="flex items-center justify-between text-xs pt-1">
                <span className="text-muted-foreground">{r.calories_per_serving || 0} سعرة</span>
                <div className="flex items-center gap-0.5">
                  <span className="text-muted-foreground text-[10px] flex items-center gap-1"><Heart className="h-3 w-3" />{r.favorites_count || 0}</span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className={`h-7 w-7 ${isDraft ? "text-emerald-600" : "text-muted-foreground"}`}
                    onClick={() => togglePublish(r)}
                    title={isDraft ? "نشر" : "إرجاع لمسودة"}
                    data-testid={`btn-toggle-publish-${r.id}`}
                  >
                    {isDraft ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => edit(r)} data-testid={`btn-edit-recipe-${r.id}`}><Pencil className="h-3 w-3" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(r.id)} data-testid={`btn-del-recipe-${r.id}`}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
            </div>
          </Card>
          );
        })}
      </div>

      <AiImportDialog open={aiOpen} onClose={() => setAiOpen(false)} onSaved={() => { setStatusFilter("draft"); load(); }} />
      <AiGenerateDialog
        open={genOpen}
        onClose={() => setGenOpen(false)}
        onJobStarted={(job) => setBgJobs((prev) => [...prev, { ...job, startedAt: Date.now() }])}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ingredients editor
// ---------------------------------------------------------------------------
function IngredientsEditor({ value, onChange }) {
  const add = () => onChange([...(value || []), { name: "", amount: "", unit: "" }]);
  const upd = (i, patch) => onChange(value.map((it, k) => k === i ? { ...it, ...patch } : it));
  const rm = (i) => onChange(value.filter((_, k) => k !== i));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>المكونات</Label>
        <Button type="button" size="sm" variant="outline" onClick={add} className="gap-1"><Plus className="h-3 w-3" /> مكوّن</Button>
      </div>
      {(value || []).length === 0 && <div className="text-xs text-muted-foreground py-2 text-center border border-dashed rounded">لا مكونات — اضغط "+ مكوّن"</div>}
      {(value || []).map((it, i) => (
        <div key={i} className="grid grid-cols-[1fr_100px_100px_auto] gap-2" data-testid={`ingredient-row-${i}`}>
          <Input placeholder="الاسم" value={it.name} onChange={(e) => upd(i, { name: e.target.value })} />
          <Input placeholder="الكمية" value={it.amount} onChange={(e) => upd(i, { amount: e.target.value })} />
          <Input placeholder="الوحدة" value={it.unit} onChange={(e) => upd(i, { unit: e.target.value })} />
          <Button type="button" size="icon" variant="ghost" onClick={() => rm(i)} className="text-destructive"><X className="h-3 w-3" /></Button>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI import dialog — upload PDF/image, extract, auto-save as drafts
// ---------------------------------------------------------------------------
function AiImportDialog({ open, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);

  const close = () => { setFile(null); onClose(); };

  const extract = async () => {
    if (!file) { toast.error("اختر ملفاً أولاً"); return; }
    setExtracting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: job } = await api.post("/recipes/admin/extract-from-file", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000, // upload only — extraction happens in background
      });
      const jobId = job.job_id;

      // Poll every 3s for up to 8 minutes.
      const start = Date.now();
      const MAX_MS = 8 * 60 * 1000;
      let saved = null;
      while (Date.now() - start < MAX_MS) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: status } = await api.get(`/recipes/admin/extract-from-file/status/${jobId}`);
        if (status.status === "done") { saved = status.recipes || []; break; }
        if (status.status === "error") { throw new Error(status.error || "فشل الاستخراج"); }
      }
      if (saved === null) throw new Error("انتهت مهلة الاستخراج — حاول بملف أصغر أو تحقّق لاحقاً.");

      toast.success(`تم استخراج وحفظ ${saved.length} وصفة كمسودة — راجعها ثم انشرها.`);
      onSaved && onSaved();
      close();
    } catch (e) {
      toast.error(extractError(e, "فشل الاستخراج. تأكد من الملف وحاول مجدداً."));
    } finally { setExtracting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent dir="rtl" className="max-w-lg" data-testid="ai-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            استخراج وصفات بالذكاء الاصطناعي (Gemini 3 Pro)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg bg-primary/5 border border-primary/20 p-4 text-sm space-y-1">
            <p className="font-medium">كيف يعمل:</p>
            <ol className="list-decimal ps-5 text-xs text-muted-foreground space-y-0.5">
              <li>ارفع ملف <b>PDF</b> فيه وصفة واحدة أو عدة وصفات، أو <b>صورة</b> لوصفة.</li>
              <li>الذكاء الاصطناعي يستخرج تلقائياً: العنوان، المكونات، الخطوات، السعرات، الماكروز، الوقت.</li>
              <li>إذا كان الملف بالإنجليزية سيُترجم كل شيء إلى العربية.</li>
              <li>تُحفظ الوصفات <b>تلقائياً كمسودات</b> — تظهر لك بشارة صفراء وليست مرئية للأعضاء حتى تنشرها.</li>
            </ol>
          </div>

          <div className="space-y-2">
            <Label>الملف (PDF أو صورة PNG/JPG/WEBP)</Label>
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/jpg,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm file:me-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
              data-testid="ai-file-input"
            />
            {file && <div className="text-xs text-muted-foreground">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={extracting}>إلغاء</Button>
            <Button onClick={extract} disabled={!file || extracting} className="gap-2" data-testid="btn-ai-extract">
              {extracting ? <><Loader2 className="h-4 w-4 animate-spin" /> جاري الاستخراج في الخلفية...</> : <><Upload className="h-4 w-4" /> استخراج وحفظ كمسودات</>}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// AI generate dialog — creates N new recipes with AI images, auto-saved as drafts
// ---------------------------------------------------------------------------
function AiGenerateDialog({ open, onClose, onJobStarted }) {
  const [count, setCount] = useState(10);
  const [category, setCategory] = useState("mixed");
  const [subcategory, setSubcategory] = useState("");  // "" = any within category
  const [healthStyle, setHealthStyle] = useState("moderate"); // regular | moderate | strict
  const [generateImages, setGenerateImages] = useState(true);
  const [loading, setLoading] = useState(false);

  const close = () => {
    setCount(10); setCategory("mixed"); setSubcategory("");
    setHealthStyle("moderate"); onClose();
  };

  const subcatOptions = category !== "mixed" ? getSubcategories(category) : [];

  const generate = async () => {
    setLoading(true);
    try {
      const { data: job } = await api.post("/recipes/admin/ai-generate", {
        count, category,
        subcategory: subcategory || null,
        health_style: healthStyle,
        generate_images: generateImages,
      }, { timeout: 30000 });

      // Fire-and-forget: hand the job off to the page-level tracker, close
      // the dialog immediately, and let the admin queue up more batches.
      const catLabel = category === "mixed" ? "مختلط"
        : category === "sweet" ? "حلو"
        : category === "savory" ? "مالح"
        : category === "beverage" ? "مشروبات"
        : "صلصات";
      const styleLabel = healthStyle === "strict" ? " · فيتنس" : " · متوازن";
      const subLabel = subcategory ? ` (${subcategory})` : "";
      const label = `${count} × ${catLabel}${subLabel}${styleLabel}`;

      onJobStarted && onJobStarted({ id: job.job_id, label });
      toast.success(
        `بدأ التوليد في الخلفية — ${label}. يمكنك بدء توليد آخر مباشرة أو متابعة عملك.`,
        { duration: 6000 },
      );
      close();
    } catch (e) {
      toast.error(extractError(e, "فشل بدء التوليد. حاول مجدداً."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent dir="rtl" className="max-w-lg" data-testid="ai-generate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            توليد وصفات صحية جديدة بالذكاء الاصطناعي
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 p-4 text-sm space-y-1">
            <p className="font-medium text-purple-900 dark:text-purple-100">كيف يعمل:</p>
            <ol className="list-decimal ps-5 text-xs text-purple-900/70 dark:text-purple-100/70 space-y-0.5">
              <li>حدد العدد والفئة (وحتى النوع تحتها إن أردت التركيز)، ثم اضغط "توليد".</li>
              <li>الذكاء الاصطناعي يولّد وصفات <b>مشهورة</b> عليها إقبال كبير — يتجنب التكرار مع وصفاتك الحالية.</li>
              <li>يقدر السعرات والماكروز والمكونات والخطوات تلقائياً بالعربية.</li>
              <li>يولّد صورة احترافية لكل وصفة (اختياري — يبطئ العملية دقيقة أو أكثر).</li>
              <li>تُحفظ الوصفات <b>تلقائياً كمسودات</b> — راجعها ثم انشرها للأعضاء.</li>
            </ol>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>عدد الوصفات (1-30)</Label>
              <Input type="number" min={1} max={30} value={count}
                     onChange={(e) => setCount(Math.max(1, Math.min(30, +e.target.value || 1)))}
                     data-testid="gen-count-input" />
            </div>
            <div className="space-y-2">
              <Label>الفئة</Label>
              <Select value={category} onValueChange={(v) => { setCategory(v); setSubcategory(""); }}>
                <SelectTrigger data-testid="gen-category-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mixed">مختلط (توزيع على كل الفئات)</SelectItem>
                  <SelectItem value="sweet">حلو فقط</SelectItem>
                  <SelectItem value="savory">مالح فقط</SelectItem>
                  <SelectItem value="beverage">مشروبات فقط</SelectItem>
                  <SelectItem value="sauces">صلصات فقط</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {category !== "mixed" && subcatOptions.length > 0 && (
            <div className="space-y-2">
              <Label>النوع تحت الفئة (اختياري)</Label>
              <Select value={subcategory || "__any"} onValueChange={(v) => setSubcategory(v === "__any" ? "" : v)}>
                <SelectTrigger data-testid="gen-subcategory-input">
                  <SelectValue placeholder="أي نوع (تنويع تلقائي)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">أي نوع (تنويع تلقائي)</SelectItem>
                  {subcatOptions.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                إذا اخترت نوعاً معيناً، سيتم توليد كل الوصفات بالضبط تحت هذا النوع.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label>مستوى الوصفة</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="gen-health-style">
              {[
                { v: "moderate", label: "🥗 يومي متوازن", hint: "استبدلات صحية ذكية — للاستخدام اليومي" },
                { v: "strict",   label: "💪 فيتنس",       hint: "بروتين عالٍ، بلا سكر مضاف أو قلي" },
              ].map((opt) => {
                const active = healthStyle === opt.v;
                return (
                  <button
                    type="button"
                    key={opt.v}
                    onClick={() => setHealthStyle(opt.v)}
                    data-testid={`gen-health-${opt.v}`}
                    className={`text-right rounded-lg border p-3 transition-all ${
                      active
                        ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40 ring-1 ring-emerald-500/50"
                        : "border-border bg-background hover:bg-accent/40"
                    }`}
                  >
                    <div className={`text-sm font-semibold ${active ? "text-emerald-700 dark:text-emerald-300" : ""}`}>
                      {opt.label}
                    </div>
                    <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                      {opt.hint}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground">
              الاثنان صحيّان — الفرق فقط في مستوى الصرامة. سيتم إضافة "فائدة" مختصرة لكل وصفة.
            </p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={generateImages}
                   onChange={(e) => setGenerateImages(e.target.checked)}
                   className="h-4 w-4 accent-purple-600" data-testid="gen-images-check" />
            <span className="text-sm">توليد صور بالذكاء الاصطناعي لكل وصفة <span className="text-muted-foreground text-xs">(أبطأ لكن يعطي شكلاً احترافياً)</span></span>
          </label>

          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={loading}>إلغاء</Button>
            <Button onClick={generate} disabled={loading} className="gap-2 bg-purple-600 hover:bg-purple-700" data-testid="btn-do-generate">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> جاري إرسال الطلب...</> : <><Sparkles className="h-4 w-4" /> ابدأ التوليد في الخلفية</>}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

